import { ClipboardList, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { TaskStatus } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NewTaskDialog } from "@/features/tasks/components/NewTaskDialog";
import { TasksTable } from "@/features/tasks/components/TasksTable";
import { useTasks } from "@/features/tasks/hooks/useTasks";

const ALL = "__all__";

const STATUS_OPTIONS: TaskStatus[] = [
  "queued",
  "running",
  "waiting_decision",
  "completed",
  "error",
  "token_limit",
  "cancelled",
];

export function TasksListPage() {
  const { t } = useTranslation("tasks");
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const { data, isLoading, isError, error, refetch } = useTasks(
    statusFilter === ALL ? {} : { status: statusFilter as TaskStatus },
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        actions={
          <Button onClick={() => setNewTaskOpen(true)}>
            <Plus className="h-4 w-4" />
            {t("actions.start")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t("filters.byStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("filters.allStatuses")}</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={t("empty.title")}
          description={t("empty.description")}
          action={
            <Button onClick={() => setNewTaskOpen(true)}>
              <Plus className="h-4 w-4" />
              {t("actions.start")}
            </Button>
          }
        />
      ) : (
        <TasksTable tasks={data} />
      )}

      <NewTaskDialog open={newTaskOpen} onOpenChange={setNewTaskOpen} />
    </div>
  );
}
